import { Builder } from '@builder.io/react';
import { BuilderComponentState } from '@builder.io/react/dist/types/src/components/builder-component.component';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';
import SwiperCore from 'swiper';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { replaceOrAddLocale, useLocale } from '../../helpers/LangHelper';
import { BusinessIndustries } from '../../types/BusinessIndustries';
import { BusinessHubIndustriesCarouselBuilderConfig } from './business-hub-industries-carousel.builder-component';
import styles from './business-hub-industries-carousel.module.scss';

const options: SwiperOptions = {
    slidesPerView: 'auto',
    allowTouchMove: true,
    centeredSlides: true,
    centeredSlidesBounds: true,
    centerInsufficientSlides: true,
    initialSlide: 1,
    freeMode: {
        enabled: true,
        sticky: false,
    },
    modules: [FreeMode],
    watchOverflow: false,
    grabCursor: true,

    breakpoints: {
        608: {
            initialSlide: 0,
            watchOverflow: true,
        },
    },
};

interface SSProps {
    builderState?: BuilderComponentState;
}

export interface BusinessHubIndustriesCarouselProps extends SSProps {
    industries?: BusinessIndustries[];
}

const BusinessHubIndustriesCarousel: FC<BusinessHubIndustriesCarouselProps> = (props) => {
    const { industries: industriesProps, builderState } = props;
    const config = builderState?.state[BusinessHubIndustriesCarouselBuilderConfig.name];

    const { locale } = useLocale();
    const router = useRouter();

    const pagePath = useMemo(() => {
        const path = Array.isArray(router.query.slug) ? router.query.slug.join('/') : router.query.slug;
        return replaceOrAddLocale(`/${path}` || '', locale);
    }, [router, locale]);

    const industries =
        useMemo(() => {
            let indus = industriesProps;
            if (Builder.isEditing || Builder.isPreviewing) {
                indus = industriesProps || (config?.industries as BusinessIndustries[]);
            }

            if (!indus) return indus;

            return indus
                .filter((item) => {
                    if (Builder.isEditing || Builder.isPreviewing) {
                        return true;
                    }

                    return !item.isInPreview;
                })
                .filter((i) => i.builderPage)
                .sort((a, b) => {
                    if (a.name < b.name) {
                        return -1;
                    }
                    if (a.name > b.name) {
                        return 1;
                    }
                    return 0;
                });
        }, [industriesProps, config]) || [];

    return (
        <div className={styles.container}>
            <Swiper
                {...options}
                onInit={(swiper: SwiperCore) => {
                    swiper.setProgress(0.5);
                }}
            >
                <SwiperSlide className={styles.mdHide}></SwiperSlide>
                <SwiperSlide>
                    <div className={styles.industries}>
                        {industries.map(({ name, builderPage }) => {
                            const href = replaceOrAddLocale((builderPage?.value?.data as any)?.url || '', locale);
                            return (
                                <Link key={name} className={classNames(styles.carouselItem, { [styles.active]: pagePath === href })} href={href}>
                                    {name}
                                </Link>
                            );
                        })}
                    </div>
                </SwiperSlide>
                <SwiperSlide className={styles.mdHide}></SwiperSlide>
            </Swiper>
        </div>
    );
};

export default BusinessHubIndustriesCarousel;
